import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, Truck } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { formatINR, resolveImage } from '@/lib/format';

export default function Cart() {
    const { items, updateQty, removeItem, subtotal, shipping, total } = useCart();
    const navigate = useNavigate();
    const remaining = Math.max(0, 499 - subtotal);

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-24 text-center">
                <div className="h-20 w-20 rounded-full bg-[var(--bg-soft)] grid place-items-center mx-auto"><ShoppingBag className="h-9 w-9 text-[var(--text-muted)]" /></div>
                <h1 className="font-display font-extrabold text-2xl mt-6">Your cart is empty</h1>
                <p className="text-[var(--text-muted)] mt-2">Looks like you haven&apos;t added anything yet.</p>
                <Link to="/shop" className="btn-primary mt-6 inline-flex h-12 px-7">Start shopping <ArrowRight className="h-4 w-4" /></Link>
            </div>
        );
    }

    return (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-14">
            <h1 className="font-display font-extrabold tracking-tight text-3xl lg:text-5xl">Your cart</h1>
            <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8">
                <div className="space-y-4">
                    {items.map((it) => (
                        <div key={it.product_id} data-testid={`cart-line-${it.slug}`} className="flex gap-4 rounded-[var(--radius-md)] border border-[var(--border)] bg-white p-4">
                            <Link to={`/product/${it.slug}`}><img src={resolveImage(it.image)} alt={it.name} loading="lazy" decoding="async" className="h-24 w-24 rounded-xl object-contain bg-[var(--bg-soft)] p-2" /></Link>
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between gap-3">
                                    <div>
                                        <Link to={`/product/${it.slug}`} className="font-display font-bold hover:text-[var(--xtra-blue)] transition-colors">{it.name}</Link>
                                        <p className="text-sm text-[var(--text-muted)]">{it.size}</p>
                                    </div>
                                    <button onClick={() => removeItem(it.product_id)} aria-label="Remove item" className="text-[var(--text-muted)] hover:text-red-500 h-fit transition-colors"><Trash2 className="h-4 w-4" /></button>
                                </div>
                                <div className="mt-3 flex items-center justify-between">
                                    <div className="inline-flex items-center rounded-full border border-[var(--border)]">
                                        <button onClick={() => updateQty(it.product_id, it.qty - 1)} className="h-9 w-9 grid place-items-center hover:bg-[var(--bg-soft)] rounded-l-full transition-colors" aria-label="Decrease"><Minus className="h-4 w-4" /></button>
                                        <span className="w-9 text-center text-sm font-bold tabular-nums">{it.qty}</span>
                                        <button onClick={() => updateQty(it.product_id, it.qty + 1)} className="h-9 w-9 grid place-items-center hover:bg-[var(--bg-soft)] rounded-r-full transition-colors" aria-label="Increase"><Plus className="h-4 w-4" /></button>
                                    </div>
                                    <span className="font-display font-extrabold tabular-nums">{formatINR(it.price * it.qty)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="h-fit rounded-[var(--radius-lg)] border border-[var(--border)] bg-white p-6 lg:sticky lg:top-28">
                    <h2 className="font-display font-bold text-lg">Order summary</h2>
                    {remaining > 0 ? (
                        <div className="mt-4 rounded-xl bg-[var(--bg-soft)] px-4 py-2.5 text-sm flex items-center gap-2"><Truck className="h-4 w-4 text-[var(--xtra-blue)]" /> Add <b>{formatINR(remaining)}</b> for free shipping</div>
                    ) : (
                        <div className="mt-4 rounded-xl bg-blue-50 px-4 py-2.5 text-sm text-[var(--xtra-blue)] flex items-center gap-2"><Truck className="h-4 w-4" /> Free shipping unlocked!</div>
                    )}
                    <div className="mt-4 space-y-3 text-sm">
                        <div className="flex justify-between"><span className="text-[var(--text-muted)]">Subtotal</span><span className="font-medium tabular-nums">{formatINR(subtotal)}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--text-muted)]">Shipping</span><span className="font-medium tabular-nums">{shipping === 0 ? 'FREE' : formatINR(shipping)}</span></div>
                        <div className="pt-3 border-t border-[var(--border)] flex justify-between text-base font-display font-extrabold"><span>Total</span><span className="tabular-nums">{formatINR(total)}</span></div>
                    </div>
                    <button onClick={() => navigate('/checkout')} data-testid="cart-checkout-button" className="btn-primary w-full h-12 mt-5">Proceed to checkout</button>
                    <Link to="/shop" className="block text-center mt-3 text-sm font-semibold text-[var(--xtra-blue)] link-underline w-fit mx-auto">Continue shopping</Link>
                </div>
            </div>
        </div>
    );
}
